import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import env from '../../config/env';

interface Review {
  id: number;
  type: 'ad_review' | 'user_review';
  subject: string;
  reason: string;
  details?: string;
  status: string;
  rating?: number;
  created_at: string;
}

interface Meta {
  total: number;
  per_page: number;
  current_page: number;
  last_page: number;
}

const PER_PAGE = 10;

const statusColor: Record<string, string> = {
  pending:  '#3b82f6',
  approved: '#22c55e',
  rejected: '#ef4444',
  reviewed: '#3b82f6',
  resolved: '#22c55e',
};

const StarRating: React.FC<{ rating: number; max?: number }> = ({ rating, max = 5 }) => (
  <div style={{ display: 'flex', gap: 1, flexShrink: 0 }}>
    {Array.from({ length: max }, (_, i) => (
      <span key={i} style={{ color: i < rating ? '#f59e0b' : '#d1d5db', fontSize: 18, lineHeight: 1 }}>★</span>
    ))}
  </div>
);

const formatDate = (dateStr: string): string => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day   = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${d.getFullYear()}`;
};

const ReviewsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params   = new URLSearchParams(location.search);
  const userId   = params.get('user_id');
  const isAr     = params.get('language') === 'ar';

  const [reviews, setReviews] = useState<Review[]>([]);
  const [meta, setMeta]       = useState<Meta>({ total: 0, per_page: PER_PAGE, current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);

  useEffect(() => {
    document.documentElement.dir = isAr ? 'rtl' : 'ltr';
  }, [isAr]);

  const fetchReviews = useCallback((p: number) => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    axios
      .get(`${env.apiUrl}/getUserReviews`, { params: { user_id: userId, page: p, per_page: PER_PAGE } })
      .then((res) => {
        if (res.data.status) {
          setReviews(res.data.reviews);
          setMeta(res.data.meta);
        }
      })
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { fetchReviews(page); }, [page, fetchReviews]);

  const goTo = (p: number) => {
    if (p < 1 || p > meta.last_page) return;
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageNumbers = (): (number | '…')[] => {
    const { current_page: cur, last_page: last } = meta;
    if (last <= 5) return Array.from({ length: last }, (_, i) => i + 1);
    const pages: (number | '…')[] = [1];
    if (cur > 3) pages.push('…');
    for (let i = Math.max(2, cur - 1); i <= Math.min(last - 1, cur + 1); i++) pages.push(i);
    if (cur < last - 2) pages.push('…');
    pages.push(last);
    return pages;
  };

  const isEmpty = !loading && reviews.length === 0;

  const reviewCount = isAr
    ? `${meta.total} ${meta.total === 1 ? 'تقييم' : 'تقييمات'}`
    : `${meta.total} review${meta.total !== 1 ? 's' : ''}`;

  return (
    <div style={{ ...styles.container, direction: isAr ? 'rtl' : 'ltr' }}>
      {loading ? (
        <p style={styles.loadingText}>{isAr ? 'جارٍ التحميل…' : 'Loading…'}</p>
      ) : isEmpty ? (
        <div style={styles.emptyContainer}>
          <h2 style={styles.emptyTitle}>{isAr ? 'لا توجد تقييمات مقدمة' : 'No Reviews Submitted'}</h2>
          <p style={styles.emptySubtitle}>{isAr ? 'لم تقدم أي تقييمات.' : 'You have not submitted any reviews.'}</p>
        </div>
      ) : (
        <>
          <h1 style={styles.pageTitle}>{isAr ? 'تقييماتك المقدمة' : 'Your Submitted Reviews'}</h1>
          <p style={styles.introText}>
            {isAr
              ? 'اطّلع على جميع الإعلانات التي قيّمتها بعد محادثات موثّقة مع البائعين / المعلنين.'
              : 'See all the listings you have rated after verified conversations with sellers / advertiser.'}
          </p>

          <div style={styles.sectionHeader}>
            <p style={styles.sectionLabel}>{isAr ? 'تفاصيل التقييمات' : 'Reviews Details'}</p>
            {meta.total > 0 && (
              <p style={styles.totalLabel}>{reviewCount}</p>
            )}
          </div>
          <div style={styles.divider} />

          {reviews.map((r, i) => (
            <div key={`${r.type}-${r.id}`}>
              <div style={styles.reviewRow}>
                <div style={styles.rowContent}>
                  <div style={styles.rowLeft}>
                    <p style={styles.reviewSubject}>
                      {isAr ? 'الإعلان المقيَّم: ' : 'Reviewed Ad: '}<strong>{r.subject}</strong>
                    </p>
                    <p style={styles.statusLine}>
                      {isAr ? 'الحالة: ' : 'Status: '}
                      <span style={{ color: statusColor[r.status.toLowerCase()] ?? '#666', fontWeight: 700 }}>
                        {r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                      </span>
                    </p>
                    <p style={styles.reviewMeta}>
                      {isAr ? 'تاريخ التقديم: ' : 'Date Submitted: '}<strong>{formatDate(r.created_at)}</strong>
                    </p>
                  </div>
                  {r.rating != null && <StarRating rating={r.rating} />}
                </div>
              </div>
              <div style={styles.rowDivider} />
            </div>
          ))}

          {meta.last_page > 1 && (
            <div style={styles.pagination}>
              <button
                style={{ ...styles.pageBtn, opacity: page === 1 ? 0.35 : 1 }}
                onClick={() => goTo(page - 1)}
                disabled={page === 1}
              >
                {isAr ? '›' : '‹'}
              </button>

              {pageNumbers().map((p, idx) =>
                p === '…' ? (
                  <span key={`ellipsis-${idx}`} style={styles.ellipsis}>…</span>
                ) : (
                  <button
                    key={p}
                    style={{ ...styles.pageBtn, ...(p === page ? styles.pageBtnActive : {}) }}
                    onClick={() => goTo(p as number)}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                style={{ ...styles.pageBtn, opacity: page === meta.last_page ? 0.35 : 1 }}
                onClick={() => goTo(page + 1)}
                disabled={page === meta.last_page}
              >
                {isAr ? '‹' : '›'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    fontFamily: "'IBM Plex Sans Arabic', 'Segoe UI', Arial, sans-serif",
    backgroundColor: '#fff',
    minHeight: '100vh',
    padding: '0 0 32px',
    boxSizing: 'border-box',
    maxWidth: 480,
    margin: '0 auto',
  },
  pageTitle: {
    fontSize: 22,
    fontWeight: 700,
    color: '#111',
    textAlign: 'center',
    margin: '8px 20px 14px',
  },
  introText: {
    fontSize: 14,
    color: '#333',
    textAlign: 'justify',
    lineHeight: 1.7,
    margin: '0 20px 28px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    padding: '0 20px',
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: '#111',
    margin: 0,
  },
  totalLabel: {
    fontSize: 12,
    color: '#888',
    margin: 0,
  },
  divider: {
    borderBottom: '2px solid #111',
    margin: '6px 20px 0',
  },
  reviewRow: {
    padding: '14px 20px',
  },
  rowContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
  },
  rowLeft: {
    flex: 1,
    minWidth: 0,
  },
  reviewSubject: {
    fontSize: 14,
    color: '#111',
    margin: '0 0 4px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  statusLine: {
    fontSize: 14,
    color: '#111',
    margin: '0 0 4px',
  },
  reviewMeta: {
    fontSize: 13,
    color: '#333',
    margin: 0,
  },
  rowDivider: {
    borderBottom: '1px solid #e5e7eb',
    margin: '0 20px',
  },
  loadingText: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginTop: 40,
  },
  emptyContainer: {
    padding: '40px 20px 0',
    textAlign: 'center',
  },
  emptyTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#2d6a4f',
    margin: '0 0 16px',
  },
  emptySubtitle: {
    fontSize: 15,
    color: '#555',
    margin: 0,
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    marginTop: 28,
    paddingBottom: 16,
  },
  pageBtn: {
    minWidth: 34,
    height: 34,
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    color: '#333',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 8px',
  },
  pageBtnActive: {
    background: '#111',
    color: '#fff',
    borderColor: '#111',
    fontWeight: 700,
  },
  ellipsis: {
    fontSize: 14,
    color: '#999',
    padding: '0 2px',
  },
};

export default ReviewsPage;
